import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器：自动附带JWT Token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器：处理401自动登出
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // 不在登录页时才跳转
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// ==================== Auth API ====================
export const authAPI = {
  register: (data: { username: string; email: string; password: string }) =>
    api.post('/auth/register', data),
  login: (data: { email: string; password: string }) =>
    api.post('/auth/login', data),
  // 手机验证码登录
  sendSmsCode: (phone: string) =>
    api.post('/auth/send-sms-code', { phone }),
  loginByPhone: (phone: string, code: string) =>
    api.post('/auth/login-phone', { phone, code }),
  // Google OAuth 登录
  loginByGoogle: (credential: string) =>
    api.post('/auth/google', { credential }),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data: { username?: string; avatar?: string }) =>
    api.put('/auth/profile', data),
};

// ==================== Tools API ====================
export const toolsAPI = {
  getTools: (params?: any) => api.get('/tools', { params }),
  getToolById: (id: number) => api.get(`/tools/${id}`),
  getToolBySlug: (slug: string) => api.get(`/tools/slug/${slug}`),
  getFeatured: () => api.get('/tools/featured'),
  trackClick: (id: number) => api.post(`/tools/${id}/click`),
  toggleFavorite: (id: number) => api.post(`/tools/${id}/favorite`),
  // 管理员
  createTool: (data: any) => api.post('/tools', data),
  updateTool: (id: number, data: any) => api.put(`/tools/${id}`, data),
  deleteTool: (id: number) => api.delete(`/tools/${id}`),
};

// ==================== Categories API ====================
export const categoriesAPI = {
  getAll: (params?: { tree?: number | string; leaves?: number | string }) =>
    api.get('/categories', { params }),
  getBySlug: (slug: string) => api.get(`/categories/${slug}`),
};

// ==================== Tasks / Rankings API ====================
export const tasksAPI = {
  getAll: (params?: { limit?: number }) => api.get('/tasks', { params }),
  getBySlug: (slug: string) => api.get(`/tasks/${slug}`),
};

export const rankingsAPI = {
  get: (list: string, params?: { limit?: number }) => api.get(`/rankings/${list}`, { params }),
};

// ==================== Reviews API ====================
export const reviewsAPI = {
  getByTool: (toolId: number, params?: any) =>
    api.get(`/reviews/tool/${toolId}`, { params }),
  create: (data: { tool_id: number; rating: number; comment: string; title?: string }) =>
    api.post('/reviews', data),
  update: (id: number, data: { rating?: number; comment?: string; title?: string }) =>
    api.put(`/reviews/${id}`, data),
  delete: (id: number) => api.delete(`/reviews/${id}`),
};

// ==================== Recommendations API ====================
export const recommendAPI = {
  get: (params?: { limit?: number }) => api.get('/recommendations', { params }),
};

// ==================== User API ====================
export const userAPI = {
  getFavorites: (params?: any) => api.get('/users/favorites', { params }),
  getReviews: () => api.get('/users/reviews'),
  getSearchHistory: (params?: { limit?: number }) => api.get('/users/search-history', { params }),
  createSearchHistory: (data: { keyword: string; result_count?: number }) =>
    api.post('/users/search-history', data),
  clearSearchHistory: () => api.delete('/users/search-history'),
};

// ==================== Deals API ====================
export const dealsAPI = {
  getDeals: (params?: any) => api.get('/deals', { params }),
  getDealById: (id: number) => api.get(`/deals/${id}`),
  getDealsByTool: (toolId: number) => api.get(`/deals/tool/${toolId}`),
  upvote: (id: number) => api.post(`/deals/${id}/upvote`),
  // 管理员
  createDeal: (data: any) => api.post('/deals', data),
  updateDeal: (id: number, data: any) => api.put(`/deals/${id}`, data),
  deleteDeal: (id: number) => api.delete(`/deals/${id}`),
};

// ==================== Admin / 合规管理 API ====================
export const adminAPI = {
  getTools: (params?: any) => api.get('/admin/tools', { params }),
  patchCompliance: (id: number, data: any) =>
    api.patch(`/admin/tools/${id}/compliance`, data),
  batchUpdate: (ids: number[], data: any) =>
    api.post('/admin/compliance/batch', { ids, data }),
  exportCompliance: (params?: any) =>
    api.get('/admin/compliance/export', { params }),
  getStats: () => api.get('/admin/compliance/stats'),
};

// ==================== Asset API (资产管理) ====================
export const assetAPI = {
  uploadLogo: (toolId: number, file: File) => {
    const formData = new FormData();
    formData.append('logo', file);
    return api.post(`/admin/assets/tools/${toolId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadScreenshots: (toolId: number, files: File[]) => {
    const formData = new FormData();
    files.forEach(f => formData.append('screenshots', f));
    return api.post(`/admin/assets/tools/${toolId}/screenshots`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteLogo: (toolId: number) =>
    api.delete(`/admin/assets/tools/${toolId}/logo`),
  deleteScreenshot: (toolId: number, idx: number) =>
    api.delete(`/admin/assets/tools/${toolId}/screenshots/${idx}`),
  getHealth: () => api.get('/admin/assets/health'),
  repair: (toolId: number) => api.post(`/admin/assets/repair/${toolId}`),
  triggerSync: (options?: { onlyMissing?: boolean; concurrency?: number; toolIds?: number[] }) =>
    api.post('/admin/assets/sync', options || { onlyMissing: true }),
};

// ==================== Order API (订单管理) ====================
export const orderAPI = {
  // Admin — /api/admin/orders
  getList: (params?: any) => api.get('/admin/orders', { params }),
  getStats: () => api.get('/admin/orders/stats'),
  getById: (id: number) => api.get(`/admin/orders/${id}`),
  create: (data: any) => api.post('/admin/orders', data),
  updatePayment: (id: number, data: any) => api.patch(`/admin/orders/${id}/payment`, data),
  updateBenefit: (id: number, data: any) => api.patch(`/admin/orders/${id}/benefit`, data),
  updateStatusByAdminToken: (id: number, data: any, adminToken: string) =>
    api.patch(`/admin/orders/${id}/status`, data, { headers: { 'x-admin-token': adminToken } }),
  // User
  getMyOrders: (params?: any) => api.get('/orders/my', { params }),
  // Public
  getToolBenefits: (toolId: number) => api.get(`/orders/tool-benefits/${toolId}`),
  createPublic: (data: any) => api.post('/orders', data),
  submitPublicPayment: (id: number, data: any) => api.post(`/orders/${id}/proof`, data),
  getPublicById: (id: number) => api.get(`/orders/${id}`),
  getPlans: () => api.get('/plans'),
  // 发起真实支付（支付宝/微信生成二维码，PayPal 返回跳转链接）
  createPayment: (id: number, method: string) => api.post(`/orders/${id}/pay`, { method }),
  // PayPal 授权返回后捕获支付
  capturePaypal: (id: number) => api.post(`/orders/${id}/paypal-capture`, {}),
  // Admin — 批量开通权益（已付款但 benefit_status=INACTIVE 的订单）
  batchGrant: (params?: { plan_type?: string; dry_run?: boolean }) =>
    api.post('/admin/orders/batch-grant', params || {}),
};

// ==================== Payment API (支付方式) ====================
export const paymentAPI = {
  getPaymentMethods: (params: { lang: string; plan?: string }) =>
    api.get('/orders/payment-methods', { params }),
  getPaymentConfig: () => api.get('/payment-config'),
  updatePaymentConfig: (data: any, adminToken: string) =>
    api.patch('/admin/payment-config', data, { headers: { 'x-admin-token': adminToken } }),
};

// ==================== Chat API (AI 客服) ====================
export const chatAPI = {
  send: (message: string, history: { role: string; content: string }[]) =>
    api.post('/chat', { message, history }),
  getSuggestions: () => api.get('/chat/suggestions'),
};

// ==================== Content API (LLM 内容管理) ====================
export const contentAPI = {
  getSchema: () => api.get('/content/schema'),
  search: (q: string) => api.get('/content/search', { params: { q } }),
  getTool: (params: { toolId?: number; slug?: string }) =>
    api.get('/content/tool', { params }),
  update: (data: { toolId?: number; slug?: string; fields: Record<string, any> }) =>
    api.post('/content/update', data),
  batchUpdate: (items: { toolId?: number; slug?: string; fields: Record<string, any> }[]) =>
    api.post('/content/batch-update', { items }),
};

// ==================== Admin / 用户管理 API ====================
export const adminUsersAPI = {
  getUsers: (params?: any) => api.get('/admin/users', { params }),
  getUserById: (id: number) => api.get(`/admin/users/${id}`),
  updateUser: (id: number, data: any) => api.put(`/admin/users/${id}`, data),
  toggleActive: (id: number) => api.patch(`/admin/users/${id}/toggle-active`),
  getDashboard: () => api.get('/admin/dashboard'),
  getTraffic: (params?: { days?: number; refresh?: number }) =>
    api.get('/admin/traffic', { params }),
  getBusinessStats: () => api.get('/admin/business-stats'),
  refreshTraffic: () => api.post('/admin/traffic/refresh'),
  exportData: (params?: { type?: string; format?: string }) =>
    api.get('/admin/export', { params }),
};

// ==================== Admin / 分类管理 API ====================
export const adminCategoriesAPI = {
  getAll: () => api.get('/admin/categories'),
  create: (data: any) => api.post('/admin/categories', data),
  update: (id: number, data: any) => api.put(`/admin/categories/${id}`, data),
  delete: (id: number) => api.delete(`/admin/categories/${id}`),
};

// ==================== Admin / 评论管理 API ====================
export const adminReviewsAPI = {
  getAll: (params?: any) => api.get('/admin/reviews', { params }),
  updateApproval: (id: number, approved: boolean) =>
    api.patch(`/admin/reviews/${id}/approval`, { is_approved: approved }),
  delete: (id: number) => api.delete(`/admin/reviews/${id}`),
};

// ==================== Feedback API ====================
export const feedbackAPI = {
  submit: (data: {
    type: string;
    message: string;
    contact?: string;
    toolId?: number;
    availabilityCnSuggestion?: string;
    evidenceUrl?: string;
    pageUrl?: string;
  }) => api.post('/feedback', data),
  getById: (id: number) => api.get(`/feedback/${id}`),
  // Admin
  getList: (params?: {
    page?: number;
    pageSize?: number;
    status?: string;
    type?: string;
    toolId?: number;
    keyword?: string;
  }) => api.get('/feedback/admin/list', { params }),
  updateStatus: (id: number, data: { status: string; adminNote?: string }) =>
    api.patch(`/feedback/admin/${id}`, data),
};

export const toolSubmissionAPI = {
  getPaymentStatus: (userId: number) => api.get(`/tool-submissions/payment-status/${userId}`),
  create: (data: any) => api.post('/tool-submissions', data),
  getMy: (userId: number) => api.get('/tool-submissions/my', { params: { userId } }),
  getAdminList: (adminToken: string, params?: any) =>
    api.get('/tool-submissions/admin/list', {
      params,
      headers: { 'x-admin-token': adminToken },
    }),
  updateAdminStatus: (id: number, data: any, adminToken: string) =>
    api.patch(`/tool-submissions/admin/${id}/status`, data, {
      headers: { 'x-admin-token': adminToken },
    }),
};
